import { useState } from "react";
import type { ConservatoryStyle } from "../../../mocks/conservatories";

interface StyleCardProps {
  style: ConservatoryStyle;
  onSelect: (style: ConservatoryStyle) => void;
  selected: boolean;
}

function RatingDots({ value, max = 5, color }: { value: number; max?: number; color: string }) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: max }).map((_, i) => (
        <span key={i} className={`inline-block w-2 h-2 rounded-full ${i < value ? color : "bg-stone-200"}`} />
      ))}
    </div>
  );
}

export default function StyleCard({ style, onSelect, selected }: StyleCardProps) {
  const [hovered, setHovered] = useState(false);

  const categoryColor: Record<string, string> = {
    classic: "bg-amber-50 text-amber-700 border-amber-200",
    premium: "bg-zinc-900 text-amber-400 border-zinc-700",
    bespoke: "bg-emerald-50 text-emerald-700 border-emerald-200",
    specialist: "bg-stone-100 text-zinc-600 border-stone-200",
  };

  return (
    <article
      className={`group relative bg-white rounded-lg overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
        selected ? "border-amber-500" : "border-stone-100 hover:border-amber-300"
      }`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onSelect(style)}
    >
      {style.badge && (
        <div className="absolute top-3 left-3 z-10 bg-amber-600 text-white text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap" style={{ fontFamily: "'Raleway', sans-serif" }}>
          {style.badge}
        </div>
      )}
      <div className="w-full h-48 overflow-hidden">
        <img
          src={style.image}
          alt={`${style.name} conservatory Surrey`}
          className={`w-full h-full object-cover object-top transition-transform duration-500 ${hovered ? "scale-105" : "scale-100"}`}
        />
      </div>
      <div className="p-5">
        <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full border capitalize mb-3 ${categoryColor[style.category]}`} style={{ fontFamily: "'Raleway', sans-serif" }}>
          {style.category}
        </span>
        <h3 className="text-lg font-bold text-zinc-800 mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          {style.name}
        </h3>
        <p className="text-xs text-amber-600 font-semibold mb-2" style={{ fontFamily: "'Raleway', sans-serif" }}>{style.tagline}</p>
        <p className="text-zinc-500 text-xs leading-relaxed mb-4" style={{ fontFamily: "'Raleway', sans-serif" }}>
          {style.description}
        </p>
        <div className="space-y-1.5 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-400" style={{ fontFamily: "'Raleway', sans-serif" }}>Floor Space</span>
            <RatingDots value={style.spaceRating} color="bg-amber-500" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-400" style={{ fontFamily: "'Raleway', sans-serif" }}>Natural Light</span>
            <RatingDots value={style.lightRating} color="bg-amber-500" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-400" style={{ fontFamily: "'Raleway', sans-serif" }}>Budget Friendly</span>
            <RatingDots value={style.budgetRating} color="bg-emerald-500" />
          </div>
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-stone-100">
          <div>
            <p className="text-xs text-zinc-400" style={{ fontFamily: "'Raleway', sans-serif" }}>From</p>
            <p className="text-base font-bold text-zinc-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{style.priceFrom}</p>
          </div>
          <button
            className={`text-xs font-semibold px-4 py-2 rounded-md transition-colors whitespace-nowrap ${
              selected ? "bg-amber-600 text-white" : "bg-stone-100 text-zinc-700 group-hover:bg-amber-600 group-hover:text-white"
            }`}
            style={{ fontFamily: "'Raleway', sans-serif" }}
          >
            {selected ? "Selected" : "View Details"}
          </button>
        </div>
      </div>
    </article>
  );
}
